import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { STATUS_COLORS } from '../constants/statusColors';
import { deriveCurrentStep, STEPS } from '../components/admin/PropertyModule';
import ModuleNotes from '../components/admin/ModuleNotes';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const QUALIFYING = ['offer_requested', 'offer_submitted', 'offer_accepted', 'option_period', 'under_contract'];
const URGENT_STAGES = ['option_period', 'under_contract'];

// Pipeline-rank — most advanced stage at the top so the team triages the
// most time-critical work first. Mirrors AdminModulesUnderwriting's
// STAGE_RANK pattern. Backend already returns intra-stage rows by
// status_updated_at; stable sort preserves that ordering for ties.
const STAGE_RANK = {
  under_contract:  0,
  option_period:   1,
  offer_accepted:  2,
  offer_submitted: 3,
  offer_requested: 4,
};

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'urgent', label: 'Urgent' },
  ...QUALIFYING.map((s) => ({ value: s, label: (STATUS_COLORS[s]?.label || s.replace(/_/g, ' ')) })),
];

// Build the object PropertyModule.deriveCurrentStep expects from a list row,
// then map the returned step number (1..STEPS.length | 'complete') to its label.
function rowStep(b) {
  const step = deriveCurrentStep({
    user: { hc_avm_value: b.hc_avm_value },
    offerRequest: {
      id: b.offer_request_id,
      listing_agent_name: b.listing_agent_name,
      listing_agent_email: b.listing_agent_email,
      offer_filename: b.offer_filename,
      manual_accept_offer_at: b.manual_accept_offer_at,
      is_new_construction: b.is_new_construction,
    },
    disclosureData: { disclosure: { status: b.disclosure_submitted ? 'submitted' : null } },
    documents: [
      ...(b.has_ab_psa ? [{ document_type: 'psa_ab_signed', offer_request_id: b.offer_request_id }] : []),
      ...(b.has_bc_psa ? [{ document_type: 'borrower_psa_fully_executed', offer_request_id: b.offer_request_id }] : []),
    ],
    loanStage: b.application_status,
    optionPeriod: { inspectify_availability_type: b.inspectify_availability_type },
    abPsa: b.ab_psa_pkg
      ? { hasPackage: true, status: b.ab_psa_pkg.status, allSignedAt: b.ab_psa_pkg.all_signed_at }
      : { hasPackage: false },
    // The Property Module's new Step 3 (Underwriting Review) gate. If
    // the backend doesn't return this signal on the list row, fall
    // through to the existing POST_OFFER_STAGES fail-open path inside
    // deriveCurrentStep so files at offer_submitted+ still surface
    // step 3 as done.
    reviewComplete: !!b.review_complete,
  });
  if (step === 'complete') return { num: STEPS.length, total: STEPS.length, label: 'Complete', isComplete: true };
  return { num: step, total: STEPS.length, label: `Step ${step} — ${STEPS[step - 1]?.label || ''}`, isComplete: false };
}

const daysAgo = (d) => {
  if (!d) return null;
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
};

export default function AdminModulesProperty() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Keep the row's notes badge in sync as notes are added/removed inline.
  const updateCount = (rowId, n) =>
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, notes_count: n } : r)));

  useEffect(() => {
    const token = localStorage.getItem('doorly_admin_token');
    axios.get(`${API}/api/admin/modules/property`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(r.data || []))
      .catch((err) => {
        const status = err.response?.status;
        const msg = err.response?.data?.error || err.message;
        setFetchError(status ? `API ${status}: ${msg}` : msg);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Column sort. Default (sortKey=null) preserves the existing
  // pipeline-rank ordering. Clicking a column header cycles
  // asc → desc → off so users can return to default without leaving.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc');
  };
  const sortIcon = (key) => {
    if (sortKey !== key) return '⇅';
    return sortDir === 'asc' ? '▲' : '▼';
  };

  const term = search.trim().toLowerCase();
  let filtered = (filter === 'all'
    ? rows
    : filter === 'urgent'
      ? rows.filter((b) => URGENT_STAGES.includes(b.application_status))
      : rows.filter((b) => b.application_status === filter))
    .filter((b) => !term || `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase().includes(term));

  if (sortKey) {
    const accessors = {
      borrower: (b) => `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase(),
      stage:    (b) => STAGE_RANK[b.application_status] ?? 99,
      days:     (b) => daysAgo(b.last_activity),
      status:   (b) => {
        const st = rowStep(b);
        const total = st.total || 1;
        const passed = st.isComplete ? total : Math.max(0, st.num - 1);
        return total > 0 ? passed / total : 0;
      },
      step:     (b) => rowStep(b).num,
    };
    const accessor = accessors[sortKey];
    filtered = filtered.slice().sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      const aN = va == null || Number.isNaN(va);
      const bN = vb == null || Number.isNaN(vb);
      if (aN && bN) return 0;
      if (aN) return 1;
      if (bN) return -1;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  } else {
    filtered = filtered.slice().sort((a, b) => {
      const ra = STAGE_RANK[a.application_status] ?? 99;
      const rb = STAGE_RANK[b.application_status] ?? 99;
      return ra - rb;
    });
  }

  const th = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb' };
  const thBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, textTransform: 'inherit', letterSpacing: 'inherit' };
  const td = { padding: '12px 14px', color: '#374151', fontSize: 13, verticalAlign: 'top' };
  const sortable = (label, key) => (
    <button type="button" onClick={() => toggleSort(key)} style={thBtn}>
      <span>{label}</span>
      <span style={{ fontSize: 9, color: sortKey === key ? '#3a6ad9' : '#9ca3af' }}>{sortIcon(key)}</span>
    </button>
  );

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Property Modules</h1>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Active acquisitions in the post-offer pipeline</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {FILTERS.map((f) => (
              <button key={f.value} onClick={() => setFilter(f.value)}
                style={{ padding: '5px 12px', borderRadius: 999, border: `1px solid ${filter === f.value ? '#3a6ad9' : '#e5e7eb'}`, background: filter === f.value ? '#eff6ff' : '#fff', color: filter === f.value ? '#3a6ad9' : '#374151', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by borrower name…"
          style={{ width: '100%', maxWidth: 360, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, marginBottom: 16, boxSizing: 'border-box' }}
        />

        {fetchError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca',
            color: '#991b1b', borderRadius: 10, padding: '12px 16px',
            marginBottom: 16, fontSize: 13,
          }}>
            <strong style={{ marginRight: 6 }}>Failed to load:</strong>{fetchError}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <thead><tr style={{ background: '#f9fafb' }}>
              <th style={th}>{sortable('Borrower',      'borrower')}</th>
              <th style={th}>{sortable('Stage',         'stage')}</th>
              <th style={th}>{sortable('Days in Stage', 'days')}</th>
              <th style={th}>{sortable('Module Status', 'status')}</th>
              <th style={th}>{sortable('Current Step',  'step')}</th>
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {filtered.map((b, i) => {
                const isUrgent = URGENT_STAGES.includes(b.application_status);
                const step = rowStep(b);
                const sc = STATUS_COLORS[b.application_status] || { bg: '#f3f4f6', text: '#374151', label: b.application_status };
                const d = daysAgo(b.last_activity);
                const expanded = expandedId === b.id;
                const count = Number(b.notes_count) || 0;
                // Module Status — completion of the Property Module.
                // Mirror AdminModulesUnderwriting's compute: passed =
                // step - 1 when in-progress (step N means N-1 are done);
                // when complete = total.
                const total  = step.total || 1;
                const passed = step.isComplete ? total : Math.max(0, step.num - 1);
                const pct    = total > 0 ? Math.round((passed / total) * 100) : 0;
                return (
                  <React.Fragment key={b.id}>
                  <tr onClick={() => setExpandedId(expanded ? null : b.id)}
                      style={{ background: expanded ? '#f3f7ff' : i % 2 ? '#fafbfc' : '#fff', borderLeft: isUrgent ? '3px solid #f59e0b' : '3px solid transparent', cursor: 'pointer' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{`${b.first_name || ''} ${b.last_name || ''}`.trim() || '—'}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{b.loan_number || `DLY-${b.id}`}</div>
                    </td>
                    <td style={td}>
                      <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.text }}>
                        {isUrgent ? '⚠ ' : ''}{sc.label}
                      </span>
                    </td>
                    <td style={{ ...td, color: '#6b7280' }}>{d === null ? '—' : `${d}d`}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 70, height: 6, borderRadius: 999, background: '#e5e7eb', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#acf44e' : '#3a6ad9' }} />
                        </div>
                        <span style={{ fontSize: 12, color: '#374151' }}>{`${pct}% (${passed}/${total})`}</span>
                      </div>
                    </td>
                    <td style={td}><span style={{ fontWeight: 500, color: '#111827' }}>{step.label}</span></td>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                        <span style={{ fontSize: 12, color: count ? '#3a6ad9' : '#9ca3af', fontWeight: count ? 600 : 400 }}>
                          💬 {count}
                        </span>
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/admin/borrowers/${b.id}?tab=property_details`); }}
                          style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                          View →
                        </button>
                        <span style={{ color: '#9ca3af', fontSize: 11 }}>{expanded ? '▲' : '▼'}</span>
                      </div>
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0, borderLeft: isUrgent ? '3px solid #f59e0b' : '3px solid transparent' }}>
                        <div style={{ padding: '16px 20px', background: '#fbfcfd', borderTop: '1px solid #eef0f2' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Notes</div>
                          <div style={{ maxWidth: 640 }}>
                            <ModuleNotes moduleType="property" borrowerId={b.id} onCountChange={(n) => updateCount(b.id, n)} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>No active property modules</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
